<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, ref, watch } from 'vue'
import {
  NumericCheckDeleteMutation,
  NumericCheckDeleteMutationVariables,
  NumericCheckSetMutation,
  NumericCheckSetMutationVariables,
} from '@/generated/graphql'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import MilliQuantityField from '@/components/input/MilliQuantityField.vue'
import { debounce } from 'lodash'
import { maxMilliQuantity, minMilliQuantity } from '@/validation'
import useVuelidate from '@vuelidate/core'

const props = defineProps<{
  value?: number
  required?: boolean
  checkConfigurationId: string
  poNumber?: string
  serialNumber?: string
  lowerLimit?: number
  upperLimit?: number
  unit?: string
}>()

const placeholder = computed(() => {
  const lower = props.lowerLimit != null ? `${props.lowerLimit / 1000}` : undefined
  const upper = props.upperLimit != null ? `${props.upperLimit / 1000}` : undefined

  let range: string | undefined
  if (lower != null && upper != null) {
    range = `${lower}-${upper}`
  } else if (lower != null) {
    range = `≥${lower}`
  } else if (upper != null) {
    range = `≤${upper}`
  }

  return [range, props.unit].filter(Boolean).join(' ') || undefined
})

const model = ref<number | undefined>(props.value)
const debouncedModel = ref<number | undefined>()
const debouncer = debounce(() => {
  debouncedModel.value = model.value
}, 1000)
watch(model, debouncer)

const validationRules = computed(() => {
  let rules: { [key: string]: object } = {}
  if (props.lowerLimit) {
    rules['lower'] = minMilliQuantity(props.lowerLimit)
  }
  if (props.upperLimit) {
    rules['upper'] = maxMilliQuantity(props.upperLimit)
  }
  return rules
})
const validation = useVuelidate(validationRules, debouncedModel)

const notifySaved = ref(false)
const setMutation = useMutation<NumericCheckSetMutation, NumericCheckSetMutationVariables>(gql`
  mutation NumericCheckSet($command: SetNumericCheckCommand!) {
    shopFloor {
      setNumericCheck(command: $command) {
        id
      }
    }
  }
`)
const deleteMutation = useMutation<NumericCheckDeleteMutation, NumericCheckDeleteMutationVariables>(
  gql`
    mutation NumericCheckDelete($command: DeleteCheckCommand!) {
      shopFloor {
        deleteCheck(command: $command)
      }
    }
  `,
)

watch(debouncedModel, (v) => {
  if (props.serialNumber == undefined || !props.poNumber) {
    return
  }
  const baseCommand = {
    checkConfigurationId: props.checkConfigurationId,
    poNumber: props.poNumber,
    serialNumber: props.serialNumber,
  }

  validation.value.$touch()
  if (!v || Number.isNaN(v) || validation.value.$invalid) {
    deleteMutation.mutate({ command: baseCommand }).then(() => {
      notifySaved.value = true
    })
    return
  }

  setMutation.mutate({ command: { ...baseCommand, milliQuantity: v } }).then(() => {
    notifySaved.value = true
  })
})
</script>

<template>
  <milli-quantity-field
    v-model="model"
    :required="props.required"
    :label="serialNumber ?? ''"
    :validation="validation"
    :placeholder="placeholder"
    persistent-placeholder
    v-bind="$attrs"
  />
  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
